/**
 * Labstep
 *
 * @module screens/ExperimentWorkflow/Show/Right
 * @desc ExperimentWorkflow Show Right
 */

import { CommentShow } from 'labstep-web/components/Comment/Show';
import EntityTimeMachine from 'labstep-web/components/Entity/TimeMachine';
import { ExperimentManager as ExperimentWorkflowExperimentManager } from 'labstep-web/components/Experiment/Manager';
import { ExperimentWorkflowCommentHeader } from 'labstep-web/components/ExperimentWorkflow/CommentHeader';
import { RightPane } from 'labstep-web/components/Layout/RightPane';
import type {
  ISubPage,
  ISubPageNested,
} from 'labstep-web/components/Layout/RightPane/types';
import {
  MetadataShow,
  MetadataShowName,
} from 'labstep-web/components/Metadata/Show';
import {
  MetadataShowThread,
  MetadataShowThreadName,
} from 'labstep-web/components/Metadata/Show/Thread';
import { ProtocolStepShowThread } from 'labstep-web/components/ProtocolStep/Show/Thread';
import {
  ProtocolValueShowContainer as ProtocolValueShow,
  ProtocolValueShowName,
} from 'labstep-web/components/ProtocolValue/Show';
import { SignatureManager } from 'labstep-web/components/Signature/Manager';
import {
  ThreadForm,
  ThreadFormViewComponent,
} from 'labstep-web/components/Thread/Form';
import { ICONS } from 'labstep-web/constants/icons';
import { IconProps } from 'labstep-web/core/Icon/types';
import { useHasFeatureFlagEnabled } from 'labstep-web/hooks/feature-flag';
import { EntityStateWorkflow } from 'labstep-web/models/entity-state-workflow.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { ExperimentWorkflowShowRightDevices } from 'labstep-web/screens/ExperimentWorkflow/Show/Right/Devices';
import { ExperimentWorkflowShowRightExperimentWorkflowLinks } from 'labstep-web/screens/ExperimentWorkflow/Show/Right/ExperimentWorkflowLinks';
import { ExperimentWorkflowEditRightMetadatas as ExperimentWorkflowShowRightMetadatas } from 'labstep-web/screens/ExperimentWorkflow/Show/Right/Metadatas';
import { ExperimentValues as ExperimentWorkflowShowRightValues } from 'labstep-web/screens/ExperimentWorkflow/Show/Right/Values';
import { LogIndex } from 'labstep-web/screens/Log/Index';
import { navigation, routing } from 'labstep-web/services/navigation';
import type React from 'react';
import {
  type RouteComponentProps,
  useRouteMatch,
} from 'react-router-dom';
import { ExperimentWorkflowShowRightEntityStateWorkflow } from './StateWorkflow';
import type { IScreensExperimentWorkflowShowRightProps } from './types';

const checkReferrer = (
  location: RouteComponentProps['location'],
  route: keyof typeof routing,
  params: Record<string, unknown>,
) =>
  (location.state as any)?.referrer?.pathname ===
  routing[route](params as any);

export const ScreensExperimentWorkflowShowRight: React.FC<
  IScreensExperimentWorkflowShowRightProps
> = ({ experimentWorkflow }) => {
  const hasSignatureWorkflowFeatureEnabled = useHasFeatureFlagEnabled(
    'signature_workflow',
  );

  const subPages: ISubPage[] = [
    {
      name: 'Protocols',
      count: experimentWorkflow.experiment_count,
      icon: ICONS.experiment.primary,
      route: {
        to: 'experiment_workflow_show_workflow',
        params: { id: experimentWorkflow.id },
      },
      component: (
        <div className="experiment-workflow-protocols">
          <ExperimentWorkflowExperimentManager
            experimentWorkflow={experimentWorkflow}
          />
        </div>
      ),
    },
    {
      name: 'Inventory',
      count: experimentWorkflow.protocol_value_count,
      icon: ICONS.protocol_value.primary,
      route: {
        to: 'experiment_workflow_show_values',
        params: { id: experimentWorkflow.id },
      },
      component: (
        <ExperimentWorkflowShowRightValues
          experimentWorkflow={experimentWorkflow}
        />
      ),
      expanded: (
        <ExperimentWorkflowShowRightValues
          experimentWorkflow={experimentWorkflow}
          tableFormat
        />
      ),
      premiumFeature: 'inventory',
    },
    {
      name: 'Data',
      icon: ICONS.metadata.default,
      count: experimentWorkflow.metadata_count,
      route: {
        to: 'experiment_workflow_show_metadata',
        params: { id: experimentWorkflow.id },
      },
      component: (
        <ExperimentWorkflowShowRightMetadatas
          experimentWorkflow={experimentWorkflow}
        />
      ),
    },
    {
      name: 'Devices',
      count: experimentWorkflow.protocol_device_count,
      icon: ICONS.device.primary,
      route: {
        to: 'experiment_workflow_show_devices',
        params: { id: experimentWorkflow.id },
      },
      component: (
        <ExperimentWorkflowShowRightDevices
          experimentWorkflow={experimentWorkflow}
        />
      ),
      premiumFeature: 'devices',
    },
    {
      name: 'Activity',
      icon: 'history',
      route: {
        to: 'experiment_workflow_show_activity',
        params: { id: experimentWorkflow.id },
      },
      component: (
        <div>
          <LogIndex
            entityName={experimentWorkflow.entityName}
            entityId={experimentWorkflow.id}
          />
          <EntityTimeMachine
            logEntity={experimentWorkflow}
            documentEntity={experimentWorkflow.root_experiment}
          />
        </div>
      ),
    },
  ];

  // Remove review and linked experiment tabs for template
  if (!experimentWorkflow.is_template) {
    subPages.splice(
      -1,
      0,
      {
        name: 'Signatures',
        count: experimentWorkflow.signatures.length,
        icon: 'edit',
        route: {
          to: 'experiment_workflow_show_signatures',
          params: { id: experimentWorkflow.id },
        },
        component: (
          <SignatureManager experimentWorkflow={experimentWorkflow} />
        ),
      },
      {
        name: 'Linked Experiments',
        count: experimentWorkflow.totalLinkCount,
        icon: ICONS.experiment_workflow.primary,
        route: {
          to: 'experiment_workflow_show_experiment_workflows',
          params: { id: experimentWorkflow.id },
        },
        component: (
          <ExperimentWorkflowShowRightExperimentWorkflowLinks
            experimentWorkflow={experimentWorkflow}
          />
        ),
      },
    );
  } else if (hasSignatureWorkflowFeatureEnabled) {
    subPages.splice(0, 0, {
      name: `${EntityStateWorkflow.getHumanReadableEntityName(
        false,
        true,
      )}`,
      icon: ICONS.entity_state_workflow.primary,
      route: {
        to: 'experiment_workflow_show_state_workflow',
        params: { id: experimentWorkflow.id },
      },
      component: (
        <ExperimentWorkflowShowRightEntityStateWorkflow
          experimentWorkflow={experimentWorkflow}
        />
      ),
    });
  }

  subPages.splice(-1, 0, {
    name: 'Notes',
    icon: ICONS.thread.primary,
    count: experimentWorkflow.comment_count,
    route: {
      to: 'experiment_workflow_show_thread',
      params: { id: experimentWorkflow.id },
    },
    component: (
      <ThreadForm
        key={JSON.stringify(experimentWorkflow.thread.id)}
        createThreadId={experimentWorkflow.thread.id}
        readThreadIds={[]}
        experimentWorkflow={experimentWorkflow}
        entityNameLabel="notes"
        header={({ comment }) => (
          <ExperimentWorkflowCommentHeader comment={comment} />
        )}
      />
    ),
    expanded: (
      <ThreadFormViewComponent
        key={JSON.stringify(experimentWorkflow.thread.id)}
        createThreadId={experimentWorkflow.thread.id}
        readThreadIds={[]}
        experimentWorkflow={experimentWorkflow}
        entityNameLabel="notes"
        header={({ comment }) => (
          <ExperimentWorkflowCommentHeader comment={comment} />
        )}
      />
    ),
  });

  const subPagesNested: ISubPageNested[] = [
    {
      icon: ICONS.protocol_value.primary,
      route: {
        to: 'experiment_workflow_show_values_outputs',
      },
      backRoute: {
        to: 'experiment_workflow_show',
        params: { id: experimentWorkflow.id },
      },
      component: () => (
        <ExperimentWorkflowShowRightValues
          experimentWorkflow={experimentWorkflow}
        />
      ),
      name: () => 'Inventory',
    },
    {
      icon: ICONS.protocol_value.primary,
      route: {
        to: 'experiment_workflow_show_value_show',
      },
      backRoute: {
        to: 'experiment_workflow_show_values',
        params: { id: experimentWorkflow.id },
      },
      component: ({ match }) => (
        <ProtocolValueShow id={match.params.childId} />
      ),
      name: ({ match }) => (
        <ProtocolValueShowName id={match.params.childId} />
      ),
    },
    {
      icon: ICONS.protocol_value.primary,
      route: {
        to: 'experiment_workflow_show_value_show_output',
      },
      backRoute: {
        to: 'experiment_workflow_show_values_outputs',
        params: { id: experimentWorkflow.id },
      },
      component: ({ match }) => (
        <ProtocolValueShow id={match.params.childId} />
      ),
      name: ({ match }) => (
        <ProtocolValueShowName id={match.params.childId} />
      ),
    },
    {
      icon: ICONS.metadata.primary,
      route: {
        to: 'experiment_workflow_show_metadata_outputs',
      },
      backRoute: {
        to: 'experiment_workflow_show',
        params: { id: experimentWorkflow.id },
      },
      component: () => (
        <ExperimentWorkflowShowRightMetadatas
          experimentWorkflow={experimentWorkflow}
        />
      ),
      name: () => 'Data',
    },
    {
      icon: ICONS.metadata.primary,
      route: {
        to: 'experiment_workflow_show_metadata_show',
      },
      backRoute: {
        to: 'experiment_workflow_show_metadata',
        params: { id: experimentWorkflow.id },
      },
      component: ({ match }) => (
        <MetadataShow
          id={match.params.childId}
          parentEntityName={Experiment.entityName}
        />
      ),
      name: ({ match }) => (
        <MetadataShowName id={match.params.childId} />
      ),
    },
    {
      icon: ICONS.metadata.primary,
      route: {
        to: 'experiment_workflow_show_metadata_show_output',
      },
      backRoute: {
        to: 'experiment_workflow_show_metadata_outputs',
        params: { id: experimentWorkflow.id },
      },
      component: ({ match }) => (
        <MetadataShow
          id={match.params.childId}
          parentEntityName={Experiment.entityName}
        />
      ),
      name: ({ match }) => (
        <MetadataShowName id={match.params.childId} />
      ),
    },
    {
      icon: ICONS.thread.primary,
      route: {
        to: 'experiment_workflow_show_thread_show',
      },
      backRoute: {
        to: 'experiment_workflow_show_thread',
        params: { id: experimentWorkflow.id },
      },
      component: ({ match }) => (
        <CommentShow id={match.params.childId} />
      ),
      name: () => 'Thread',
    },
    {
      icon: ICONS.thread.primary,
      route: {
        to: 'experiment_workflow_show_metadata_show_thread',
      },
      backRoute: ({ location }) => ({
        to: checkReferrer(
          location,
          'experiment_workflow_show_thread',
          { id: experimentWorkflow.id },
        )
          ? 'experiment_workflow_show_thread'
          : 'experiment_workflow_show_metadata_show',
        params: { id: experimentWorkflow.id },
      }),
      component: ({ match }) => (
        <MetadataShowThread id={match.params.childId} />
      ),
      name: ({ match }) => (
        <MetadataShowThreadName id={match.params.childId} />
      ),
    },
    {
      icon: ICONS.thread.primary,
      route: {
        to: 'experiment_workflow_show_step_show_thread',
      },
      backRoute: {
        to: 'experiment_workflow_show_thread',
        params: { id: experimentWorkflow.id },
      },
      component: ({ match }) => (
        <ProtocolStepShowThread id={match.params.childId} />
      ),
      name: () => 'Notes',
    },
    {
      icon: ICONS.thread.primary,
      route: {
        to: 'experiment_workflow_show_metadata_show_thread_show',
      },
      backRoute: ({ location }) => ({
        to: checkReferrer(
          location,
          'experiment_workflow_show_thread',
          { id: experimentWorkflow.id },
        )
          ? 'experiment_workflow_show_thread'
          : 'experiment_workflow_show_metadata_show_thread',
        params: { id: experimentWorkflow.id },
      }),
      component: ({ match }) => (
        <CommentShow id={match.params.threadId} />
      ),
      name: () => 'Thread',
    },
    {
      icon: ICONS.thread.primary,
      route: {
        to: 'experiment_workflow_show_step_show_thread_show',
      },
      backRoute: {
        to: 'experiment_workflow_show_step_show_thread',
        params: { id: experimentWorkflow.id },
      },
      component: ({ match }) => (
        <CommentShow id={match.params.threadId} />
      ),
      name: () => 'Thread',
    },
  ];

  const mainPage = {
    route: {
      to: 'experiment_workflow_show' as keyof typeof routing,
      params: { id: experimentWorkflow.id },
    },
  };

  return (
    <RightPane
      route={mainPage.route}
      subPages={subPages}
      subPagesNested={subPagesNested}
    />
  );
};

export default ScreensExperimentWorkflowShowRight;
